import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { whatsappService, WhatsAppContact } from "@/services/whatsapp";
import { MessageCircle, Loader2, User } from "lucide-react";

interface WhatsAppContactPickerProps {
  onSelect: (data: { name: string; phone: string }) => void;
  disabled?: boolean;
  className?: string;
}

function extractPhone(waId: string): string {
  return waId.split("@")[0];
}

export function WhatsAppContactPicker({
  onSelect,
  disabled,
  className,
}: WhatsAppContactPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const PAGE_SIZE = 50;

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["whatsapp-contacts"],
    queryFn: ({ pageParam }) =>
      whatsappService.getChatContactsPage({
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled: open,
    staleTime: 60_000,
  });

  const contacts: WhatsAppContact[] =
    data?.pages.flatMap((page) => page.items) ?? [];

  const term = search.trim().toLowerCase();
  const filtered = term
    ? contacts.filter(
        (c) =>
          c.name?.toLowerCase().includes(term) ||
          c.pushname?.toLowerCase().includes(term) ||
          extractPhone(c.id).includes(term)
      )
    : contacts;

  const handleSelect = async (contact: WhatsAppContact) => {
    setResolvingId(contact.id);
    let phone = extractPhone(contact.id);
    try {
      const result = await whatsappService.getPhoneByLid(contact.id);
      if (result?.pn) {
        phone = extractPhone(result.pn);
      }
    } catch {
      // El lid no existe/no se pudo resolver, se usa el id del contacto como telefono
    } finally {
      setResolvingId(null);
    }

    onSelect({
      name: contact.name || contact.pushname || extractPhone(contact.id),
      phone,
    });
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={className ?? "h-9 w-9 shrink-0 text-success"}
          disabled={disabled}
          title="Seleccionar contacto de WhatsApp"
        >
          <MessageCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="center">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nombre..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList
            onWheel={(e) => {
              // El Popover se renderiza en un portal fuera del árbol del
              // Dialog que lo contiene, por lo que react-remove-scroll
              // bloquea el scroll nativo con la rueda; lo aplicamos a mano.
              const el = e.currentTarget;
              el.scrollTop += e.deltaY;

              const nearBottom =
                el.scrollHeight - el.scrollTop - el.clientHeight < 80;
              if (nearBottom && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : isError ? (
              <CommandEmpty>Error al cargar contactos de WhatsApp</CommandEmpty>
            ) : filtered.length === 0 ? (
              <CommandEmpty>No se encontraron contactos</CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map((contact, idx) => {
                  const displayName =
                    contact.name || contact.pushname || extractPhone(contact.id);
                  return (
                    <CommandItem
                      key={`${contact.id}-${idx}`}
                      value={`${contact.id}-${idx}`}
                      onSelect={() => handleSelect(contact)}
                      disabled={resolvingId !== null}
                      className="flex items-center gap-2"
                    >
                      <Avatar className="h-8 w-8 shrink-0">
                        {contact.picture && (
                          <AvatarImage src={contact.picture} alt="" />
                        )}
                        <AvatarFallback>
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-1 flex-col overflow-hidden">
                        <span className="truncate">{displayName}</span>
                        {contact.lastMessagePreview && (
                          <span className="text-xs text-muted-foreground truncate">
                            {contact.lastMessagePreview}
                          </span>
                        )}
                      </div>
                      {resolvingId === contact.id && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                      )}
                    </CommandItem>
                  );
                })}
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  </div>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
